/**
 * CareMap global carousel analytics (client).
 *
 * Approaches (for comparing message effectiveness by target):
 * 1. Current: Firestore aggregates per slideId + callable increments (impression / click / signup).
 *    Signup uses sessionStorage attribution set on internal-path clicks from the carousel.
 * 2. BigQuery / GA4: log events with slide_id + target; analyze in BI tools; no Firestore counters.
 * 3. Multi-armed bandits: server chooses next slide weights from live CTR (future).
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import { isValidMapCarouselSlideId } from "@healthdesk/shared-utils";
import { createButtonClickForHref, isAllowedCarouselHref } from "./mapCarouselHref";

const SLIDE_ID_STORAGE_KEY = "carousel_attribution_slide";

const HTTP_PREFIX = /^https?:\/\//i;
const MAILTO_PREFIX = /^mailto:/i;

export function stashCarouselSlideForSignupAttribution(slideId) {
  try {
    if (slideId && typeof slideId === "string") {
      sessionStorage.setItem(SLIDE_ID_STORAGE_KEY, slideId);
    }
  } catch (e) {
    console.warn("stashCarouselSlideForSignupAttribution failed", e);
  }
}

/**
 * @returns {string | null}
 */
export function consumeCarouselSlideSignupAttribution() {
  try {
    const v = sessionStorage.getItem(SLIDE_ID_STORAGE_KEY);
    if (v) {
      sessionStorage.removeItem(SLIDE_ID_STORAGE_KEY);
    }
    return v;
  } catch (e) {
    console.warn("consumeCarouselSlideSignupAttribution failed", e);
    return null;
  }
}

/**
 * @param {{ slideId: string, kind: 'impression' | 'click' | 'signup' }} args
 */
export function formatCarouselStatsLine(stats) {
  const imp = Number(stats?.impressions) || 0;
  const clk = Number(stats?.clicks) || 0;
  const sup = Number(stats?.signups) || 0;
  const ctr = imp > 0 ? `${((clk / imp) * 100).toFixed(1)}%` : "—";
  const signupPerImp = imp > 0 ? `${((sup / imp) * 100).toFixed(2)}%` : "—";
  const signupPerClick = clk > 0 ? `${((sup / clk) * 100).toFixed(1)}%` : "—";
  return `Impr ${imp} · Clicks ${clk} · Signups ${sup} · CTR ${ctr} · Signup/impr ${signupPerImp} · Signup/click ${signupPerClick}`;
}

/**
 * Records a carousel CTA click and stashes slide id for same-origin signup attribution (matches global carousel).
 * @param {string} href
 * @param {string} slideId
 */
export function recordCarouselClickAndStashForInternalPath(href, slideId) {
  if (!isValidMapCarouselSlideId(slideId)) {
    return;
  }
  void emitMapCarouselEvent({ slideId, kind: "click" });
  const t = typeof href === "string" ? href.trim() : "";
  if (
    t &&
    !HTTP_PREFIX.test(t) &&
    !MAILTO_PREFIX.test(t) &&
    t.startsWith("/")
  ) {
    stashCarouselSlideForSignupAttribution(slideId);
  }
}

export async function emitMapCarouselEvent({ slideId, kind }) {
  if (!slideId || typeof slideId !== "string" || !kind) {
    return;
  }
  if (!isValidMapCarouselSlideId(slideId)) {
    return;
  }
  try {
    const fn = httpsCallable(getFunctions(), "recordMapCarouselEvent");
    await fn({ slideId, kind });
  } catch (e) {
    console.warn("emitMapCarouselEvent failed", e);
  }
}

/**
 * Wraps global carousel navigation: records click, stashes slide id for signup attribution on same-origin paths.
 * @param {string} href
 * @param {string} slideId
 * @returns {() => void}
 */
export function createCarouselGlobalButtonClick(href, slideId) {
  return () => {
    void emitMapCarouselEvent({ slideId, kind: "click" });

    const t = typeof href === "string" ? href.trim() : "";
    if (!isAllowedCarouselHref(t)) {
      console.warn("mapCarousel: blocked or invalid href", href);
      return;
    }

    if (slideId && !HTTP_PREFIX.test(t) && !MAILTO_PREFIX.test(t) && t.startsWith("/")) {
      stashCarouselSlideForSignupAttribution(slideId);
    }

    createButtonClickForHref(t)();
  };
}
